import { Component, DestroyRef, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalFunctions } from '@global/GlobalFunctions';
import { AuthenticationService, IWebAppUser } from '@services/authentication.service';
import { FilesService } from '@services/files.service';
import { IIntegratonSensor, IntegrationSensorsService, SensorDatapoint, WebhookSensor } from '@services/integration-sensors.service';
import { IntegrationsService, IWebhookIntegration } from '@services/integrations.service';
import { NeuronsensorsService } from '@services/neuronsensors.service';
import { siteSubscriptions } from '@services/siteSubscriptions';
import { Location } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IAccount } from '@services/accounts.service';
import { IOrganisation } from '@services/organisation-data.service';
import { combineLatest, map, distinctUntilChanged, filter, firstValueFrom } from 'rxjs';
import { VirtualSensor, VirtualSensorsService } from '@services/virtual-sensors.service';

@Component({
	selector: 'app-data-integrations-virtualsensors-manage',
	templateUrl: './data-integrations-virtualsensors-manage.component.html',
	styleUrl: './data-integrations-virtualsensors-manage.component.scss'
})
export class DataIntegrationsVirtualsensorsManageComponent {

	/** Observable Subject Finalise */
	private destroyRef = inject(DestroyRef);

	/** Current User */
	public currentUser: IWebAppUser

	/** Route Organisation ID */
	public routeOrganisationID: string

	/** Selected Organisation */
	public selectedOrganisation: IOrganisation

	/** Selected Account GUID */
	public selectedAccountGUID: string

	/** Selected Account */
	public selectedAccount: IAccount

	/** Array of Webhooks */
	public webhookIntegrations: IWebhookIntegration[]

	/** Selected Integration ID */
	public selectedIntegrationID: string = "";

	/** Selected Integration */
	public selectedIntegration: IWebhookIntegration

	/** virtualSensors */
	public virtualSensors: VirtualSensor[] = [];

	/** virtualSensorFields */
	public virtualSensorFields: Object = { text: "VirtualSensorName", id: "VirtualSensorID" };

	/** selectedVirtualSensors */
	public selectedVirtualSensors: VirtualSensor[] = [];

	/** integrationSensors */
	public integrationSensors: IIntegratonSensor[] = [];

	/** integrationSensorFields */
	public integrationSensorFields: Object = { text: "Name", id: "WebhookSensorID" };

	/** selectedIntegrationSensors */
	public selectedIntegrationSensors: IIntegratonSensor[] = [];

	/**
	 * 
	 * @param globalFunctions 
	 * @param authenticationService 
	 * @param activatedRoute 
	 * @param siteSubscriptions 
	 * @param integrationService 
	 * @param integrationSensorsService 
	 * @param router 
	 * @param location 
	 * @param dialog 
	 * @param neuronsensorsService 
	 * @param filesService 
	 */
	constructor(
		private globalFunctions: GlobalFunctions,
		public authenticationService: AuthenticationService,
		private activatedRoute: ActivatedRoute,
		private siteSubscriptions: siteSubscriptions,
		private integrationService: IntegrationsService,
		private integrationSensorsService: IntegrationSensorsService,
		private virtualSensorsService: VirtualSensorsService,
		private router: Router,
		private location: Location,
		public dialog: MatDialog,
		private neuronsensorsService: NeuronsensorsService,
		private filesService: FilesService,
	) {
	}

	// -------------------------------------------------------------------------
	// Initialisation
	// -------------------------------------------------------------------------

	/**
	 * getChildRoute
	 * Recursively traverse the ActivatedRoute tree to find the deepest child route.
	 * @param route 
	 * @returns 
	 */
	private getChildRoute(route: ActivatedRoute): ActivatedRoute {
		while (route.firstChild) {
			route = route.firstChild;
		}

		return route;
	}

	/**
		 * ngOnInit
		 */
	ngOnInit(): void {

		const childRoute = this.getChildRoute(this.activatedRoute);

		/*
		 * Initialise the selected Account / Organisation.
		 * These values are required before we can load the integrations.
		 */
		combineLatest([
			this.authenticationService.currentUser$,
			this.siteSubscriptions.myAccounts$,
			this.siteSubscriptions.myOrganisations$,
			this.activatedRoute.paramMap,
			childRoute.paramMap.pipe(
				map(params => params.get('integrationID') || ''),
				distinctUntilChanged(),
				takeUntilDestroyed(this.destroyRef))
		]).pipe(
			takeUntilDestroyed(this.destroyRef),
			filter(([currentUser, myAccounts, myOrganisations, paramMap, childParamMap]) =>
				!!currentUser && !!myAccounts && !!myOrganisations && !!paramMap && !!childParamMap
			)
		).subscribe({
			next: async ([currentUser, myAccounts, myOrganisations, paramMap]) => {
				try {
					// Check we have a validated User
					if (currentUser) {
						this.currentUser = <IWebAppUser>currentUser;
					}

					// Get the GUID for the selected Organisation to Manage
					if (paramMap.has('organisation')) {

						if (this.routeOrganisationID != paramMap.get('organisation')) {

							this.routeOrganisationID = paramMap.get('organisation') || "";

							if (this.routeOrganisationID != "") {

								const organisationsList = myOrganisations;

								this.selectedOrganisation = <IOrganisation>organisationsList.find(
									({ Uid }) => Uid.toUpperCase() === this.routeOrganisationID.toUpperCase()
								);
							}

							const selectedAccount = myAccounts.find(
								a => a.GUID == this.selectedOrganisation.AccountGUID
							);

							if (selectedAccount) {
								this.selectedAccount = this.siteSubscriptions.normalizeFeatures(selectedAccount) as IAccount;

								this.selectedAccountGUID = selectedAccount.GUID;
							}

							this.selectedIntegrationID = childRoute.snapshot.paramMap.get('integrationID') || "";

							await this.getIntegrations(
								this.selectedAccountGUID,
								this.selectedOrganisation.Uid
							);

							await this.getVirtualSensors(
								this.selectedAccountGUID,
								this.selectedOrganisation.Uid
							);

							if (this.selectedIntegrationID != "") {
								this.selectedIntegration = this.webhookIntegrations.find(
									(x: IWebhookIntegration) => x.WebhookUID == this.selectedIntegrationID
								) ?? <IWebhookIntegration>{};
								this.integrationSensors = await this.getIntegrationSensors(this.selectedIntegrationID);
							}
						}
					}
				}
				catch (error) {
				}
				finally {
					this.filterVirtualSensors();
				}
			},
			error: (error) => {
				this.globalFunctions.HandleError(
					error,
					`Error: ${this.constructor.name} → account/organisation initialisation`,
					false
				);
			}
		});
	}

	/**
	 * getIntegrations
	 * Get the Integrations for the Account Organisation filtered by Type
	 * @param accountGUID
	 * @param organisationUID
	 * @param typeID
	 */
	async getIntegrations(accountGUID: string, organisationUID: string, typeID: number = 0) {

		let queryParams = {
			"typeID": typeID
		}

		try {
			const integrations = await firstValueFrom(
				this.integrationService.getWebhookIntegrations$(accountGUID, organisationUID, "", queryParams).pipe(
					filter((integrations) => !!integrations))
			);

			if (integrations) {

				if (integrations.length == 0) {
					this.webhookIntegrations = [];
					return;
				} else {
					this.webhookIntegrations = <IWebhookIntegration[]>integrations;
				}
			}
		}
		catch (error) {
			this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.integrationService.getWebhookIntegrations$`, false);
		} finally { this.globalFunctions.displaySpinner(false); }

	};

	/**
	 * getVirtualSensors
	 * @param accountGUID accountGUID 
	 * @param organisationUID organisationUID 
	 * @returns 
	 */
	async getVirtualSensors(accountGUID: string, organisationUID: string) {
		try {
			const virtualSensors = await firstValueFrom(
				this.virtualSensorsService.getVirtualSensor$(accountGUID, organisationUID).pipe(
					filter((virtualSensors) => !!virtualSensors))
			);

			if (virtualSensors) {

				if (virtualSensors.length == 0) {
					this.virtualSensors = [];
					return;
				} else {
					this.virtualSensors = <VirtualSensor[]>virtualSensors;
				}
			}
		}
		catch (error) {
			this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.virtualSensorsService.getVirtualSensor$`, false);
		} finally { this.globalFunctions.displaySpinner(false); }

	};

	/**
	 * selectVirtualSensor
	 * @param event event  
	 */
	selectVirtualSensor(event: any) {
		if (event.isChecked) {
			if (event.data) {
				this.selectedVirtualSensors.push(event.data);
			}
		} else {
			this.selectedVirtualSensors = this.selectedVirtualSensors.filter((vs: VirtualSensor) => vs.VirtualSensorID != event.data.VirtualSensorID);
		}
	}

	/**
	 * selectIntegrationSensor
	 * @param event event  
	 */
	selectIntegrationSensor(event: any) {
		if (event.isChecked) {
			if (event.data) {
				this.selectedIntegrationSensors.push(event.data);
			}
		} else {
			this.selectedIntegrationSensors = this.selectedIntegrationSensors.filter((is: IIntegratonSensor) => is.WebhookSensorID != event.data.WebhookSensorID);
		}
	}

	/**
	 * createWebhookSensorsFromVirtualSensors
	 */
	async createWebhookSensorsFromVirtualSensors() {
		for (const vs of this.selectedVirtualSensors) {
			let newDP: SensorDatapoint = {
				publicSenorID: `virtual_${vs.VirtualSensorID}`,
				name: `virtual_${vs.VirtualSensorID}`,
				pvName: vs.VirtualSensorName,
				description: "",
				pv: 1,
				si: vs.SI ?? "",
				timestamp: new Date().getTime(),
				value: 0,
				neuronSensorType: 0
			};
			let newWS: WebhookSensor = {
				publicSensorID: `virtual_${vs.VirtualSensorID}`,
				sensorName: vs.VirtualSensorName,
				sensorDescription: vs.Conversion,
				type: 'virtual', //"omron",
				neuronSensorType: 0,
				dataPoints: [newDP]
			};

			/** Add the sensor to the Database */
			const result = await firstValueFrom(
				this.integrationSensorsService.PutIntegrationSensor$(
					this.selectedAccountGUID,
					this.selectedOrganisation.Uid,
					this.selectedIntegrationID,
					newWS.publicSensorID,
					JSON.stringify(newWS)
				)
			);

			const message = result?.[0]?.Result?.replace("OK - ", "");

			if (message) {
				this.globalFunctions.DisplaySnackBar({
					message,
					duration: 2000,
					action: ""
				});
			}
		}
		this.refreshLists();
	}

	/** back */
	back() {
		this.location.back();
	}

	/**
	 * getIntegrationSensors
	 * Retrieve the list of sensors for a given webhook integration ID.
	 * @param routeWebhookID
	 */
	async getIntegrationSensors(routeWebhookID: string): Promise<IIntegratonSensor[]> {
		try {
			// Call the Account Service to get the Organisations
			const sensors = await firstValueFrom(
				this.integrationSensorsService.getIntegrationSensors$(this.selectedAccountGUID, this.routeOrganisationID, routeWebhookID, {}).pipe(
					filter((sensors) => !!sensors)
				)
			);

			return <IIntegratonSensor[]>sensors ?? [];

		}
		catch (error: any) {
			if (error.status != 404) {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → this.integrationSensorsService.getIntegrationSensors$`, false);
			}
			return <IIntegratonSensor[]>[];
		} finally { }
	}

	/**
	 * filterVirtualSensors on existing sensors on the integration
	 */
	filterVirtualSensors() {
		this.virtualSensors = this.virtualSensors
			.filter((vs: VirtualSensor) => !this.integrationSensors
				.map((is: IIntegratonSensor) => is.PublicSensorID)
				.includes(`virtual_${vs.VirtualSensorID}`));
	}

	/**
	 * requestDeleteIntegrationSensor
	 * @returns 
	 */
	async requestDeleteIntegrationSensor(): Promise<void> {
		const sensors = this.selectedIntegrationSensors;

		if (!sensors.length) {
			return;
		}

		let message = [
			`Are you sure you want to remove the following sensors from this integration?`,
			`\n${sensors.map(x => x.Name?.trim() || x.PublicSensorID).join(', ')}`,
			`\nAssociated Datapoints and Rules will be removed from linked Assets and Locations.`,
			`This action cannot be undone.`
		].filter(s => !!s).join('\n');

		const OKResult = await firstValueFrom(
			this.globalFunctions.UserConfirm$(
				message,
				`Remove Sensors`,
				"Remove",
				undefined,
				"750px"
			)
		);
		if (OKResult) {
			try {
				await this.deleteIntegrationSensor(sensors);
			}
			catch (error: any) {
				this.globalFunctions.HandleError(error, `Error: ${this.constructor.name} → deleteObsoleteSensor`, false);
			}
		}
	}

	/**
	 * deleteIntegrationSensor
	 * @param sensors sensors 
	 */
	async deleteIntegrationSensor(sensors: IIntegratonSensor[]) {
		for (const sensor of sensors) {
			const result = await firstValueFrom(
				this.integrationSensorsService.deleteIntegrationSensor$(
					this.selectedAccountGUID,
					this.selectedOrganisation.Uid,
					this.selectedIntegrationID,
					sensor.SensorID
				)
			);

			const message = result?.[0]?.Result?.replace("OK - ", "");

			if (message) {
				this.globalFunctions.DisplaySnackBar({
					message,
					duration: 2000,
					action: ""
				});
			}
		}
		this.refreshLists();
	}

	/**
	 * refreshLists
	 */
	async refreshLists() {
		this.selectedVirtualSensors = [];
		this.selectedIntegrationSensors = [];

		await this.getVirtualSensors(
			this.selectedAccountGUID,
			this.selectedOrganisation.Uid
		);

		this.integrationSensors = await this.getIntegrationSensors(this.selectedIntegrationID);

		this.filterVirtualSensors();
	}
}
